import {
  ICertification,
  ISimulation,
  TSimulationStatus,
  TCertificationStatus,
  ApiCertification,
  ApiAvailables,
  ApiScenario,
  ApiSession,
  ApiScenarioLock,
  TSimulationType,
  ISimulationPlanningCard,
} from '@/types';
import { TIconName } from '@/design-tokens';
import { mockPersonas } from './personas.mock';
import { mockApiScoreCards } from './scores.mock';

/**
 * Certification titles and categories used in the app
 */
const CERTIFICATION_TITLES = [
  'Foundation Skills',
  'Advanced Skills',
  'Dupixent: Advanced Skills',
  'Dermatology Excellence',
  'Clinical Acumen Mastery',
  'Strategic Account Management',
] as const;

const CERTIFICATION_CATEGORIES = [
  'Communication Skills',
  'Product Knowledge',
  'Clinical Excellence',
  'Sales Mastery',
  'Relationship Building',
  'Strategic Thinking',
] as const;

const ISSUING_TEAMS = [
  'Immunology (Dermatology) Learning Excellence Team',
  'Respiratory Learning Excellence Team',
  'Oncology Learning Excellence Team',
  'General Medicine Learning Excellence Team',
  'Strategic Account Learning Excellence Team',
] as const;

/**
 * Helper function to get certification title by index
 */
function getCertificationTitle(index: number): string {
  return CERTIFICATION_TITLES[index % CERTIFICATION_TITLES.length];
}

/**
 * Helper function to get certification category by index
 */
function getCertificationCategory(index: number): string {
  return CERTIFICATION_CATEGORIES[index % CERTIFICATION_CATEGORIES.length];
}

/**
 * Helper function to get issuing team by index
 */
function getIssuingTeam(index: number): string {
  return ISSUING_TEAMS[index % ISSUING_TEAMS.length];
}

/**
 * Helper function to generate a realistic due date
 */
function generateDueDate(index: number): string {
  const baseDate = new Date('2026-03-01');
  const daysOffset = index * 30 + Math.random() * 14; // 30-44 days spread
  baseDate.setDate(baseDate.getDate() + daysOffset);
  return baseDate.toISOString();
}

/**
 * Helper function to generate simulation status based on progress
 */
function generateSimulationStatus(
  simulationIndex: number,
  certProgress: number,
): TSimulationStatus {
  if (simulationIndex < certProgress) {
    return 'completed';
  } else if (simulationIndex === certProgress) {
    return 'available';
  } else {
    return 'available';
  }
}

/**
 * Helper function to generate planning cards for simulations
 */
function generatePlanningCards(
  simulationType: TSimulationType,
  certId: number,
  personaId: number,
): ISimulationPlanningCard[] {
  const baseCards: ISimulationPlanningCard[] = [
    {
      stepNumber: 1,
      title: 'Review Objectives',
      actionLabel: 'View Learning Goals',
      iconName: 'ListChecks' as TIconName,
      content: [
        {
          type: 'singular',
          sections: [
            {
              order: 1,
              title: 'Learning Objectives',
              description: 'Review what you will accomplish in this simulation.',
              items: [
                'Master key conversation techniques',
                'Apply product knowledge effectively',
                'Build rapport with stakeholders',
                'Handle common objections',
              ],
            },
          ],
        },
      ],
    },
    {
      stepNumber: 2,
      title: 'Prepare Materials',
      actionLabel: 'Access Resources',
      iconName: 'Strategy' as TIconName,
      content: [
        {
          type: 'multiple',
          sections: [
            {
              order: 1,
              title: 'Key Resources',
              items: [
                'Product information sheets',
                'Clinical trial data',
                'Competitive analysis',
                'Customer insights',
              ],
            },
            {
              order: 2,
              title: 'Preparation Tips',
              description: 'Best practices for simulation success',
              items: [
                {
                  item: 'Practice your elevator pitch',
                  description: 'Be ready to summarize key benefits in 30 seconds',
                },
                {
                  item: 'Review objection responses',
                  description: 'Prepare for common concerns and questions',
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  // Add simulation-specific cards
  const simulationSpecificCards: Record<TSimulationType, ISimulationPlanningCard[]> = {
    practice: [
      {
        stepNumber: 3,
        title: 'Practice Environment',
        actionLabel: 'Enter Safe Space',
        iconName: 'Sparkle' as TIconName,
        content: [
          {
            type: 'singular',
            sections: [
              {
                order: 1,
                title: 'Safe Learning Environment',
                description: 'This is a practice session where you can learn without pressure.',
                items: [
                  'No scoring or evaluation',
                  'Focus on skill development',
                  'Receive constructive feedback',
                  'Take your time to practice',
                ],
              },
            ],
          },
        ],
      },
      {
        stepNumber: 4,
        title: 'Get Feedback',
        actionLabel: 'Review Performance',
        iconName: 'ChatCenteredText' as TIconName,
        content: [
          {
            type: 'singular',
            sections: [
              {
                order: 1,
                title: 'Learning Feedback',
                description: 'Use this feedback to improve your skills.',
                items: [
                  'Review conversation highlights',
                  'Identify improvement areas',
                  'Practice suggested techniques',
                  'Prepare for role-play scenarios',
                ],
              },
            ],
          },
        ],
      },
    ],
    'role-play': [
      {
        stepNumber: 3,
        title: 'Meet Your Persona',
        actionLabel: 'Learn About Customer',
        iconName: 'User' as TIconName,
        personaId: personaId.toString(),
        content: [
          {
            type: 'singular',
            sections: [
              {
                order: 1,
                title: 'Persona Background',
                description: 'Understand who you will be speaking with.',
                items: [
                  'Professional background and experience',
                  'Current challenges and pain points',
                  'Communication style preferences',
                  'Decision-making criteria',
                ],
              },
            ],
          },
        ],
      },
      {
        stepNumber: 4,
        title: 'Role-Play Conversation',
        actionLabel: 'Start Simulation',
        iconName: 'VideoCamera' as TIconName,
        content: [
          {
            type: 'multiple',
            sections: [
              {
                order: 1,
                title: 'Simulation Guidelines',
                items: [
                  'Engage in natural conversation',
                  'Apply your preparation materials',
                  'Stay in character throughout',
                  'Focus on building rapport',
                ],
              },
              {
                order: 2,
                title: 'Success Tips',
                items: [
                  {
                    item: 'Listen actively',
                    description: 'Pay attention to verbal and non-verbal cues',
                  },
                  {
                    item: 'Ask good questions',
                    description: 'Uncover needs and build understanding',
                  },
                  {
                    item: 'Handle objections gracefully',
                    description: 'Address concerns with empathy and evidence',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        stepNumber: 5,
        title: 'Review Performance',
        actionLabel: 'Get Scorecard',
        iconName: 'Medal' as TIconName,
        content: [
          {
            type: 'singular',
            sections: [
              {
                order: 1,
                title: 'Performance Evaluation',
                description: 'Review your scored performance and feedback.',
                items: [
                  'Detailed scorecard with ratings',
                  'Specific feedback on key skills',
                  'Recommendations for improvement',
                  'Unlock next simulation level',
                ],
              },
            ],
          },
        ],
      },
    ],
    certification: [
      {
        stepNumber: 3,
        title: 'Final Assessment',
        actionLabel: 'Begin Certification',
        iconName: 'Medal' as TIconName,
        content: [
          {
            type: 'singular',
            sections: [
              {
                order: 1,
                title: 'Certification Requirements',
                description: 'This is your final assessment for certification.',
                items: [
                  'Comprehensive skill evaluation',
                  'Must meet minimum score requirements',
                  'Demonstrates mastery of all objectives',
                  'Counts toward official certification',
                ],
              },
            ],
          },
        ],
      },
      {
        stepNumber: 4,
        title: 'Earn Certification',
        actionLabel: 'Complete Assessment',
        iconName: 'CheckCircle' as TIconName,
        content: [
          {
            type: 'multiple',
            sections: [
              {
                order: 1,
                title: 'Assessment Criteria',
                items: [
                  'Communication effectiveness',
                  'Product knowledge application',
                  'Objection handling skills',
                  'Professional presence',
                ],
              },
              {
                order: 2,
                title: 'Upon Completion',
                description: 'What happens after successful certification',
                items: [
                  {
                    item: 'Official certification badge',
                    description: 'Digital credential for your profile',
                  },
                  {
                    item: 'Performance summary',
                    description: 'Detailed breakdown of your assessment',
                  },
                  {
                    item: 'Next steps guidance',
                    description: 'Recommendations for continued learning',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return [...baseCards, ...simulationSpecificCards[simulationType]];
}

/**
 * Helper function to create a simulation object
 */
function createSimulation(
  id: number,
  certId: number,
  simulationIndex: number,
  type: TSimulationType,
  certProgress: number,
  statusOverride?: TSimulationStatus,
): ISimulation {
  const status = generateSimulationStatus(simulationIndex, certProgress);
  const personaId = (simulationIndex % mockPersonas.length) + 1;

  const titles = {
    practice: `Practice Certification`,
    'role-play': `Role-Play Scenario ${simulationIndex + 1}`,
    certification: 'Final Certification',
  };

  const descriptions = {
    practice: `Practice your skills in a safe environment. This session focuses on core competencies and provides feedback to help you prepare for the role-play scenarios.`,
    'role-play': `Engage in a realistic conversation scenario with our AI-powered persona. This simulation tests your ability to apply skills in a practical setting.`,
    certification: `Final comprehensive assessment that validates your mastery of all required skills and competencies for this certification.`,
  };

  const categories = {
    practice: `You’re about to begin the Practice Certification. We know certifications can be stressful, so this practice session is designed to help you sharpen your skills before the final certification. This scenario uses the same content and assessment rubric you’ll encounter on the final, giving you an accurate rehearsal of the experience. Results from this practice will be shared with your manager so you can work together to improve. Practice as often as you like — move to the final certification when you feel confident. Good luck!.`,
    'role-play': `Use this role-play simulation to safely practice a realistic interaction, to sharpen your skills and boost your confidence for real-world conversations. Before you begin, utilize the below details to pre-call plan and get the most from your practice.`,
    certification: `You are about to start the official final certification. If you require additional preparation, feel free to repeat the certification practice scenario before proceeding.

    This certification evaluates your proficiency in delivering approved messaging in a compliant and impactful manner, demonstrating clinical/product knowledge, and leveraging LEAD selling techniques.

    Before you begin, review the details below and complete a brief pre-call plan. Start when ready — successful completion is required to earn certification. You will have 2 attempts to pass the certification. Best of luck!`,
  };

  return {
    id,
    uuid: `${id}`,
    title: titles[type],
    titleShort: titles[type],
    description: descriptions[type],
    category: categories[type],
    type,
    status: statusOverride ?? status,
    personaId,
    duration: type === 'certification' ? '25-35 minutes' : '15-20 minutes',
    emCode: `EM${certId}${String(id).padStart(3, '0')}`,
    rubricId: `rubric-${certId}-${type}`,
    objectives: [
      'Demonstrate effective communication skills',
      'Apply product knowledge appropriately',
      'Build rapport and trust with stakeholders',
      'Handle objections professionally',
    ],
    instructions: `Complete this ${type} simulation to advance your certification progress. Pay attention to the feedback and use it to improve your performance.`,
    completedAt:
      (statusOverride ?? status) === 'completed'
        ? new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)
        : undefined,
    progress:
      (statusOverride ?? status) === 'completed'
        ? 100
        : (statusOverride ?? status) === 'available'
          ? 0
          : undefined,
    history:
      type === 'certification'
        ? ['4']
        : type === 'practice'
          ? ['3']
          : mockApiScoreCards
              .filter((sc) => sc.scenario_id !== 3)
              .filter((sc) => sc.scenario_id !== 4)
              .map((score) => score.session_id.toString()),
    planningCards: generatePlanningCards(type, certId, personaId),
  };
}

/**
 * Generate ICertification mock data
 */
export function generateMockCertification(index: number = 0): ICertification {
  const id = index + 1;
  const title = getCertificationTitle(index);
  const category = getCertificationCategory(index);
  const issuingTeam = getIssuingTeam(index);

  // Generate progress (0-4, where 4 means completed)
  const maxProgress = 4; // 2 practice + 1 role-play + 1 certification
  const progress = Math.floor(Math.random() * (maxProgress + 1));

  // Determine status based on progress
  let status: TCertificationStatus;
  if (progress === 0) {
    status = 'not-started';
  } else if (progress === maxProgress) {
    status = 'completed';
  } else if (new Date() > new Date(generateDueDate(index))) {
    status = 'overdue';
  } else {
    status = 'in-progress';
  }

  // Create simulations
  const simulations: ISimulation[] = [];
  let simId = id; // Base simulation ID

  // Role-play simulation (2)
  for (let i = 0; i < 2; i++) {
    simulations.push(createSimulation(simId++, id, i, 'role-play', progress));
  }

  // Practice simulations (1)
  // DO NOT PUSH TO SIMULATIONS ARRAY - PRACTICE SIMULATION IS TRACKED SEPARATELY
  const certPractice = createSimulation(simId++, id, 2, 'practice', progress);

  // Certification simulation (1)
  // DO NOT PUSH TO SIMULATIONS ARRAY - CERTIFICATION SIMULATION IS TRACKED SEPARATELY
  const certSimulation = createSimulation(simId++, id, 3, 'certification', progress, 'completed');

  // Create certification object
  const certification: ICertification = {
    id,
    uuid: `${id}`,
    title,
    titleShort: title.replace(/:.*/g, '').trim(), // Remove subtitle after colon
    subtitle: `Master ${category.toLowerCase()} through hands-on practice`,
    description: `Comprehensive certification program designed to enhance your ${category.toLowerCase()}. Through a series of practice sessions, role-play scenarios, and final assessment, you'll develop the confidence and competency needed to excel in real-world situations.`,
    category,
    status,
    progress,
    dueDate: generateDueDate(index),
    issuingTeam,
    personas: mockPersonas,
    simulations,
    certificationPractice: certPractice,
    certificationSimulation: certSimulation,
  };

  return certification;
}

/**
 * Generate an array of ICertification mock objects
 */
export function generateMockCertifications(count: number = 4): ICertification[] {
  return Array.from({ length: count }, (_, index) => generateMockCertification(index));
}

/**
 * Default mock certifications array
 */
export const mockCertifications: ICertification[] = generateMockCertifications();

export const mockApiCertifications: ApiCertification[] = Array.from({ length: 4 }, (_, i) => {
  const certIndex = i + 1;
  const title = `${getCertificationTitle(certIndex)}`;

  return {
    id: certIndex,
    brand_id: 1,
    title,
    description: `This is the description for ${title}. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent vel risus eget eros fermentum volutpat.`,
    version: '1.0',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
});

export const mockApiAvailables: ApiAvailables[] = mockCertifications.map((cert) => ({
  category: cert.category || null,
  certificationPractice: cert.certificationPractice || null,
  certificationSimulation: cert.certificationSimulation || null,
  description: cert.description || null,
  dueDate: cert.dueDate,
  id: cert.id,
  issuingTeam: cert.issuingTeam || null,
  personas: mockPersonas.filter((persona) => {
    return cert.simulations.some((sim) => sim.personaId === persona.id);
  }),
  progress: cert.progress,
  simulations: cert.simulations,
  status: cert.status,
  subtitle: cert.subtitle as string | null,
  title: cert.title as string,
  titleShort: cert.titleShort as string | null,
  uuid: cert.uuid,
}));

export const mockApiScenarios: ApiScenario[] = mockCertifications.flatMap((cert, idx) => {
  return cert.simulations.map((sim) => ({
    id: sim?.id ? sim.id : idx,
    certification_id: cert.id,
    persona_id: sim.personaId,
    rubric_id: parseInt(sim.rubricId.replace('rubric-', ''), 10),
    description: sim.description || null,
    core_selling_skills: null,
    objectives: sim.objectives ? sim.objectives.join('||') : null,
    how_to_practice: null,
    is_practice: sim.type === 'practice',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    type:
      sim.type === 'certification'
        ? 'certification'
        : sim.type === 'practice'
          ? 'practice'
          : 'role-play',
  }));
});

export const mockApiSessions: ApiSession[] = [
  {
    id: 1,
    enrollment_id: 1,
    scenario_id: 1,
    owner_user_id: 'user123',
    status: 'completed',
    attempt_no: 1,
    started_at: '2024-01-15T00:00:00Z',
    submitted_at: '2024-01-15T01:30:00Z',
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-01-15T01:30:00Z',
  },
  {
    id: 2,
    enrollment_id: 1,
    scenario_id: 2,
    owner_user_id: 'user123',
    status: 'in_progress',
    attempt_no: 1,
    started_at: '2024-01-20T00:00:00Z',
    submitted_at: null,
    created_at: '2024-01-20T00:00:00Z',
    updated_at: '2024-01-20T00:00:00Z',
  },
  {
    id: 3,
    enrollment_id: 2,
    scenario_id: 3,
    owner_user_id: 'user123',
    status: 'completed',
    attempt_no: 1,
    started_at: '2024-01-25T00:00:00Z',
    submitted_at: '2024-01-25T02:15:00Z',
    created_at: '2024-01-25T00:00:00Z',
    updated_at: '2024-01-25T02:15:00Z',
  },
];

export const mockApiScenarioLocks: ApiScenarioLock[] = [
  {
    id: 1,
    user_id: 'user123',
    scenario_id: 1,
    is_locked: false,
    created_at: '',
    updated_at: '',
  },
  {
    id: 2,
    user_id: 'user123',
    scenario_id: 2,
    is_locked: false,
    created_at: '',
    updated_at: '',
  },
  {
    id: 3,
    user_id: 'user123',
    scenario_id: 3,
    is_locked: false,
    created_at: '',
    updated_at: '',
  },
  {
    id: 4,
    user_id: 'user123',
    scenario_id: 4,
    is_locked: true,
    created_at: '',
    updated_at: '',
  },
  {
    id: 5,
    user_id: 'user456',
    scenario_id: 1,
    is_locked: false,
    created_at: '',
    updated_at: '',
  },
];
